import { Center } from '@chakra-ui/react';
import { ListItemText } from 'app/components/ChakraTableList';
import { AudioPlayControls } from 'app/components/Song/Audio/AudioPlayControls';
import { useAudioPlayHook } from 'app/components/Song/Audio/AudioPlayControls/AudioPlayHook';
import { getFormattedDuration } from 'app/components/Song/Audio/AudioTableList/utils';
import { AudioTitle } from 'app/components/Song/Audio/AudioTitle';
import { Td, Tr } from 'app/components/Table';
import { useAppDispatch } from 'app/store';
import { setQueueIndexAction } from 'app/store/AudioPlayer/slice';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import { useCallback } from 'react';

interface AudioPlayerPlaylistTableRowProps {
  audio: AudioPlayerAudioInstance;
  index: number;
  setViewedNoteAudio: (
    value: React.SetStateAction<AudioPlayerAudioInstance | null>
  ) => void;
}

export const AudioPlayerPlaylistTableRow = ({
  audio,
  index: rowIndex,
  setViewedNoteAudio,
}: AudioPlayerPlaylistTableRowProps) => {
  const dispatch = useAppDispatch();
  const setQueueIndex = useCallback(
    (index: number) => dispatch(setQueueIndexAction(index)),
    [dispatch]
  );

  const audioFileDuration = getFormattedDuration(audio.duration);
  const isLoading = ({ isAudioLoading, audioPlayerGuid }) =>
    isAudioLoading && audio.playlistAudioGuid === audioPlayerGuid;

  const { audioGuid, parentGuid, songStatus, songTitle } = audio;
  const songDetails = { title: songTitle, status: songStatus! };
  const playlistAudioGuid = audio.playlistAudioGuid!;
  const handleAudioLoad = () => setQueueIndex(rowIndex);

  const audioPlay = useAudioPlayHook({
    audioGuid,
    parentGuid: parentGuid!,
    songDetails,
    handleAudioLoad,
    playlistAudioGuid,
  });

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      data-group
      onDoubleClick={audioPlay.handlePlay}
    >
      <Td position="relative">
        {audio.audioGuid ? (
          <>
            <AudioPlayControls
              isLoading={isLoading}
              audioGuid={audioGuid}
              playlistAudioGuid={playlistAudioGuid}
              parentGuid={parentGuid!}
              handleAudioLoad={handleAudioLoad}
              isPlayerTheme
              sx={{
                zIndex: 1,
                visibility: audioPlay.isActive ? 'visible' : 'hidden',
                _groupHover: {
                  visibility: 'visible',
                },
              }}
            />
            {audioPlay.isActive ? (
              ''
            ) : (
              <Center position="absolute" top={0} lineHeight="48px" minW={5}>
                {rowIndex + 1}
              </Center>
            )}
          </>
        ) : (
          <Center>{rowIndex}</Center>
        )}
      </Td>
      <Td>
        <AudioTitle
          audioGuid={audioGuid}
          playlistAudioGuid={playlistAudioGuid}
          title={audio.audioTitle || ''}
          isLatestVersion={false}
          isPlayerTheme
        />
      </Td>
      <Td>
        <ListItemText
          primary={audio.songTitle}
          title={audio.songTitle}
          data-testid="audio-song-title"
        />
      </Td>
      <Td>
        {audio.note && (
          <ListItemText
            title={audio.note}
            primary={audio.note}
            data-testid="audio-note-button"
            primaryTextProps={{
              cursor: 'pointer',
              color: 'textSubdued',
              maxWidth: '255px',
              onClick: (e) => {
                e.stopPropagation();
                setViewedNoteAudio(audio);
              },
            }}
          />
        )}
      </Td>
      <Td>
        <ListItemText
          primary={audioFileDuration || ''}
          data-testid="audio-duration"
        />
      </Td>
      <Td className="gap" />
    </Tr>
  );
};
